import { assert, definedMap } from "@glideapps/ts-necessities";
import { GridSelection } from "@glideapps/glide-data-grid";
import { castDraft } from "immer";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { TableName } from "common/database-strings";
import {
    TemporaryColumnData,
    updateEditedColumn,
    EditedColumn,
    DataColumnKind,
    isTemporaryDataColumnData,
    NCMPerformanceAnalysisPayload,
} from "../store/tables-state";
import { defined } from "common/Support";
import { TableColumn, isPrimitiveType, TypeSchema, isComputedColumn } from "common/generator/description";
import {
    decomposeAll,
    SyntheticColumnKind,
    defaultFormatNumberSpecification,
    decomposeFormatFormula,
    getDefaultFormatForTypeKind,
    ValueFormatSpecification,
    defaultFormatDateTimeSpecification,
    makeFormatFormula,
} from "common/generator/formulas/formula-specifications";
import { defaultRootState } from "./default-root-state";
import { setSelectedColumn as setSelectedColumnUIState, setSelectedComponent } from "./uistate";
import { SelectedComponentPath } from "store/types";
import { RecomputeTiming, TemporaryComputedColumn } from "common/components/types";

// Math and "Regular" number columns always have a number format, all others primitive
// columns have the empty format once they've been edited, but we don't have a
// specification for that.
function sanitizeColumnFormatInEditedColumn(editedColumn: EditedColumn): EditedColumn {
    function withFormat(
        defaultFormat: ValueFormatSpecification,
        otherDefaultFormat?: ValueFormatSpecification
    ): EditedColumn {
        let format = editedColumn.temporaryColumnFormat;
        if (format === undefined && editedColumn.originalColumn?.displayFormula !== undefined) {
            format = decomposeFormatFormula(editedColumn.originalColumn?.displayFormula);
        }
        if (
            format?.kind !== defaultFormat.kind &&
            (otherDefaultFormat === undefined || format?.kind !== otherDefaultFormat.kind)
        ) {
            format = defaultFormat;
        }
        const finalFormat = format ?? defaultFormat;
        return {
            ...editedColumn,
            temporaryColumnFormat: format ?? defaultFormat,
            newColumn: definedMap(editedColumn.newColumn, c => ({
                ...c,
                displayFormula: definedMap(finalFormat, makeFormatFormula),
            })),
        };
    }

    const { temporaryColumn } = editedColumn;
    if (temporaryColumn.kind === SyntheticColumnKind.Math) {
        return withFormat(defaultFormatNumberSpecification, defaultFormatDateTimeSpecification);
    } else if (temporaryColumn.kind === SyntheticColumnKind.GeoDistance) {
        return withFormat({ ...defaultFormatNumberSpecification, unit: " km", unitAfterNumber: true });
    } else if (temporaryColumn.kind === SyntheticColumnKind.YesCode) {
        const defaultFormat = definedMap(editedColumn.newColumn?.type.kind, getDefaultFormatForTypeKind);
        if (defaultFormat !== undefined) {
            return withFormat(defaultFormat);
        }
    } else if (isTemporaryDataColumnData(temporaryColumn)) {
        const defaultFormat = getDefaultFormatForTypeKind(temporaryColumn.spec.typeKind);
        if (defaultFormat !== undefined) {
            return withFormat(defaultFormat);
        }
    }

    return {
        ...editedColumn,
        temporaryColumnFormat: undefined,
    };
}

interface SetTemporaryColumnFromColumnPayload {
    readonly column: TableColumn;
    readonly originalSchema: TypeSchema;
}

interface AddTemporaryColumnFormatPayload {
    readonly column: TableColumn;
    readonly spec: ValueFormatSpecification | undefined;
}

interface UpdateTemporaryColumnPayload {
    readonly name: string | undefined;
    readonly displayName: string | undefined;
    readonly update: Partial<TemporaryColumnData>;
    readonly isValid: boolean;
    // `undefined` means don't change
    readonly newColumn: TableColumn | undefined;
}

interface UpdateComputationModelInfoPayload {
    readonly computationModelInfo: TemporaryComputedColumn | undefined;
}

const tablesSlice = createSlice({
    name: "tables",
    initialState: defaultRootState.tables,
    reducers: {
        updateTemporaryColumnFormatAction: (
            state,
            action: PayloadAction<Partial<ValueFormatSpecification> | undefined>
        ) => {
            if (state.editedColumn?.temporaryColumnFormat === undefined) return;

            const update = action.payload;
            state.editedColumn = castDraft(
                sanitizeColumnFormatInEditedColumn(
                    updateEditedColumn(state.editedColumn, undefined, undefined, {}, update, undefined)
                )
            );
        },
        setTemporaryColumnFromColumn: (state, action: PayloadAction<SetTemporaryColumnFromColumnPayload>) => {
            const { column: col, originalSchema } = action.payload;
            if (state.editedColumn !== undefined || col.formula === undefined) return;

            const formula = defined(col.formula);
            const spec = decomposeAll(formula);
            if (spec === undefined) return;

            state.editedColumn = castDraft(
                sanitizeColumnFormatInEditedColumn({
                    name: col.name,
                    displayName: col.displayName ?? col.name,
                    originalColumn: col,
                    originalSchema,
                    temporaryColumn: {
                        kind: spec.kind,
                        spec,
                    } as TemporaryColumnData,
                    temporaryColumnFormat: undefined,
                    computationModelInfo: undefined,
                    newColumn: col,
                })
            );
        },
        setShowAllData: (state, action: PayloadAction<boolean>) => {
            state.showAllData = action.payload;
        },
        setColumnFilterAction: (state, action: PayloadAction<string>) => {
            state.columnFilter = action.payload;
        },
        setSelectedTableAction: (state, action: PayloadAction<TableName | undefined>) => {
            if (action.payload !== state.selectedTable) {
                state.selectedTable = action.payload;
                state.editedColumn = undefined;
            }
        },
        addTemporaryColumnAction: (state, action: PayloadAction<TableColumn>) => {
            if (state.editedColumn !== undefined) return;
            const newColumn = action.payload;
            const name = newColumn.name;

            let temporaryColumn: TemporaryColumnData;
            if (isComputedColumn(newColumn)) {
                const formula = defined(newColumn.formula);
                const spec = decomposeAll(formula);
                if (spec === undefined) return;

                temporaryColumn = {
                    kind: spec.kind,
                    spec,
                } as any;
            } else {
                assert(isPrimitiveType(newColumn.type));
                temporaryColumn = {
                    kind: DataColumnKind.Regular,
                    spec: {
                        typeKind: newColumn.type.kind,
                    },
                };
            }

            const temporaryColumnFormat = definedMap(newColumn.displayFormula, decomposeFormatFormula);

            state.editedColumn = castDraft(
                sanitizeColumnFormatInEditedColumn({
                    name,
                    displayName: name,
                    originalColumn: undefined,
                    originalSchema: undefined,
                    temporaryColumn,
                    temporaryColumnFormat,
                    computationModelInfo: undefined,
                    newColumn,
                })
            );
            state.isValid = false;
        },
        addTemporaryColumnFormatAction: (state, action: PayloadAction<AddTemporaryColumnFormatPayload>) => {
            const { column, spec } = action.payload;
            if (state.editedColumn !== undefined) return;
            if (!isPrimitiveType(column.type)) return;
            state.editedColumn = castDraft(
                sanitizeColumnFormatInEditedColumn({
                    name: column.name,
                    displayName: column.displayName ?? column.name,
                    originalColumn: column,
                    originalSchema: undefined,
                    temporaryColumn: {
                        kind: column.isUserSpecific === true ? DataColumnKind.UserSpecific : DataColumnKind.Regular,
                        spec: { typeKind: column.type.kind },
                    },
                    // this will be set by `sanitizeColumnFormatInEditedColumn` if it's undefined
                    temporaryColumnFormat: spec,
                    computationModelInfo: undefined,
                    // the format will be updated by `sanitizeColumnFormatInEditedColumn`
                    newColumn: column,
                })
            );
            state.isValid = true;
        },
        removeTemporaryColumnAction: state => {
            state.editedColumn = undefined;
            state.isValid = false;
        },
        updateTemporaryColumnAction: (state, action: PayloadAction<UpdateTemporaryColumnPayload>) => {
            if (state.editedColumn === undefined) return;
            const { name, displayName, update, isValid, newColumn } = action.payload;
            state.editedColumn = castDraft(
                sanitizeColumnFormatInEditedColumn(
                    updateEditedColumn(state.editedColumn, name, displayName, update, {}, newColumn)
                )
            );
            state.isValid = isValid;
        },
        updateComputationModelInfoAction: (state, action: PayloadAction<UpdateComputationModelInfoPayload>) => {
            if (state.editedColumn === undefined) return;
            state.editedColumn.computationModelInfo = action.payload.computationModelInfo;
        },
        setSelectedRows: (state, action: PayloadAction<readonly number[]>) => {
            state.selectedRows = action.payload as number[];
        },
        setGridSelection: (state, action: PayloadAction<GridSelection | undefined>) => {
            state.gridSelection = action.payload as any;
        },
        setSelectedColumn: (state, action: PayloadAction<string | undefined>) => {
            state.selectedColumn = action.payload;
        },
        setTriggerNCMPerformanceAnalysis: (state, action: PayloadAction<NCMPerformanceAnalysisPayload>) => {
            state.triggerNCMPermanceAnalysis = action.payload;
        },
        setNCMPerformanceStats: (state, action: PayloadAction<RecomputeTiming | undefined>) => {
            state.ncmTimingStats = castDraft(action.payload);
        },
    },
    extraReducers: {
        [setSelectedComponent.type]: (state, action: PayloadAction<SelectedComponentPath | undefined>) => {
            if (action.payload === undefined) return;

            state.editedColumn = undefined;
            state.isValid = false;
        },
        [setSelectedColumnUIState.type]: (state, action: PayloadAction<SelectedComponentPath | undefined>) => {
            if (action.payload === undefined) return;

            state.editedColumn = undefined;
            state.isValid = false;
        },
    },
});

export const {
    addTemporaryColumnAction,
    addTemporaryColumnFormatAction,
    removeTemporaryColumnAction,
    setSelectedTableAction,
    setColumnFilterAction,
    setShowAllData,
    setTemporaryColumnFromColumn,
    updateTemporaryColumnAction,
    updateTemporaryColumnFormatAction,
    updateComputationModelInfoAction,
    setSelectedColumn,
    setSelectedRows,
    setGridSelection,
    setTriggerNCMPerformanceAnalysis,
    setNCMPerformanceStats,
} = tablesSlice.actions;
export default tablesSlice.reducer;
